import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { FormDatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { POOL } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';

import { BudgetCalculationSection } from './budget-calculation-section';
import { ChannelSummary } from './channel-summary';

export function PoolForm() {
  const intl = useIntl();
  const { hookForm, setCurrentStep } = useJobPostingContext();
  const { control, watch } = hookForm;

  const [isExpanded, setIsExpanded] = useState(true);

  const campaignType =
    watch('pool.campaignType') === 'email'
      ? intl.formatMessage({
          defaultMessage: 'Email campaign',
          description: 'Email campaign type option',
        })
      : intl.formatMessage({
          defaultMessage: 'SMS campaign',
          description: 'SMS campaign type option',
        });

  const handleEditClick = () => {
    setCurrentStep(2);
  };

  const handleCloseClick = () => {
    setIsExpanded(false);
  };

  if (!isExpanded)
    return <ChannelSummary channelSlug={POOL} setIsExpanded={setIsExpanded} />;

  return (
    <div className="border-fx-neutral-200 mb-2 w-[640px] rounded-lg border bg-white px-12 py-6 shadow-sm">
      <div className="flex flex-1 flex-col gap-6">
        <ChannelIcon
          channelSlug={POOL}
          variant="large"
          className="mx-auto"
          channelName={getChannelName(POOL)}
        />

        {/* Campaign type */}
        <Input
          value={campaignType}
          label={intl.formatMessage({
            defaultMessage: 'Campaign type',
            description: 'Label for campaign type selection',
          })}
          readOnly
          required
        />

        <div className="flex gap-6">
          {/* Campaign start date */}
          <div className="flex-1">
            <FormDatePicker
              control={control}
              name="pool.startsAt"
              label={intl.formatMessage({
                defaultMessage: 'Start date',
                description: 'Label for start date input',
              })}
              readOnly
              required
            />
          </div>

          {/* Campaign end date */}
          <div className="flex-1">
            <FormDatePicker
              control={control}
              name="pool.endAt"
              label={intl.formatMessage({
                defaultMessage: 'End date',
                description: 'Label for end date input',
              })}
              readOnly
              placeholder={
                watch('pool.isNeverEnding')
                  ? intl.formatMessage({
                      defaultMessage: 'Never expires',
                      description: 'Placeholder for never expires input',
                    })
                  : ''
              }
              required
            />
          </div>
        </div>

        <BudgetCalculationSection
          channelSlug={POOL}
          unitBudget={null}
          totalBudget={null}
          className="border-fx-neutral-200 bg-fx-neutral-50 rounded-lg border p-4"
          isPool
        />

        <div className="ml-auto flex gap-2">
          <Button type="button" variant="secondary" onClick={handleEditClick}>
            <FormattedMessage
              defaultMessage="Edit"
              description="Edit campaign details"
            />
          </Button>
          <Button type="button" onClick={handleCloseClick}>
            <FormattedMessage
              defaultMessage="Close"
              description="Close the pool form"
            />
          </Button>
        </div>
      </div>
    </div>
  );
}
